import { computed, type MaybeRefOrGetter } from 'vue';
import type { NullableDateTime } from '@/api';

/**
 * Composable that returns Bootstrap classes based on deadline proximity
 *
 * @param deadline - The deadline date (can be null/undefined)
 * @param fallbackClass - The class to return if the deadline is not set, or sufficiently in the future
 * @param isMet - Whether the deadline was met.
 * @returns Computed ref with Bootstrap classes for deadline styling
 */
export function useDeadlineClass(
    deadline: MaybeRefOrGetter<NullableDateTime | undefined>,
    fallbackClass?: string,
    isMet: MaybeRefOrGetter<boolean> = false,
) {
    return computed(() => {
        const deadlineValue = toValue(deadline);

        // If no deadline is set, return no classes
        if (!deadlineValue) {
            return fallbackClass || '';
        }

        if (toValue(isMet)) {
            return 'text-success';
        }

        const deadlineDate = new Date(deadlineValue);
        const now = new Date();
        const diffTime = deadlineDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        // If deadline has passed, return danger class
        if (diffDays < 0) {
            return 'text-danger';
        }

        // If deadline is within 7 days, return danger class
        if (diffDays <= 7) {
            return 'text-danger';
        }

        // If deadline is within 14 days, return warning class
        if (diffDays <= 14) {
            return 'text-warning';
        }

        // If deadline is more than 14 days away, return no special class
        return fallbackClass || '';
    });
}
